/**
 * The vendor console's routes (`vendor.ts`; `routes.ts` spreads these into
 * `ROUTES`), plus the two announcement reads outside the vendor tenant.
 *
 * Every `/vendor/*` route is the vendor tenant's only, tenant-wide:
 * `vendor.ops` reads, `vendor.admin` writes (D27). `vendor.*` grants exist
 * only in the vendor tenant (`apps/cloud-api/src/auth/grants.ts`), so a
 * tenant admin is refused `forbidden` at the gate; the handlers also demand
 * a `VendorAccess` proof (`apps/cloud-api/src/tenancy/vendorRepo.ts`).
 * Every write is recorded in the vendor's activity log.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import type { RouteDef } from './routes.js';
import {
  announcementPathWireSchema,
  announcementResponseWireSchema,
  createAnnouncementRequestWireSchema,
  listAnnouncementsResponseWireSchema,
  listPublicAnnouncementsResponseWireSchema,
  listVendorActivityQueryWireSchema,
  listVendorActivityResponseWireSchema,
  listVendorAnnouncementsResponseWireSchema,
  listVendorTenantsResponseWireSchema,
  pinResponseWireSchema,
  reactivateTenantRequestWireSchema,
  setPinRequestWireSchema,
  sitePinPathWireSchema,
  suspendTenantRequestWireSchema,
  tenantPathWireSchema,
  updateAnnouncementRequestWireSchema,
  vendorClientsQueryWireSchema,
  vendorClientsResponseWireSchema,
  vendorOpsResponseWireSchema,
  vendorReleasesResponseWireSchema,
  vendorTenantDetailResponseWireSchema,
  vendorTenantResponseWireSchema,
  clearNodeRejectionResponseWireSchema,
  nodeRejectionPathWireSchema,
  createVendorTenantRequestWireSchema,
  createdVendorTenantResponseWireSchema,
  updateVendorTenantRequestWireSchema,
  vendorTenantRecordResponseWireSchema,
} from './vendor.js';
import {
  ringResponseWireSchema,
  rolloutActionPathWireSchema,
  rolloutActionRequestWireSchema,
  rolloutResponseWireSchema,
  setRingRequestWireSchema,
  vendorRingsResponseWireSchema,
  vendorRolloutResponseWireSchema,
} from './rollouts.js';

const VENDOR_ONLY = 'Vendor tenant only.';

export const VENDOR_ROUTES = {
  listVendorTenants: {
    id: 'listVendorTenants',
    method: 'GET',
    path: '/vendor/tenants',
    tag: 'vendor',
    summary: 'Tenants, with their status and counts',
    description: `Every tenant of this regional deployment (D12) with its status and counts: users, sites, active nodes, live productions, and cloud-production hours this month. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: listVendorTenantsResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  getVendorTenant: {
    id: 'getVendorTenant',
    method: 'GET',
    path: '/vendor/tenants/{tenantId}',
    tag: 'vendor',
    summary: 'One tenant, in detail',
    description: `The tenant with its counts, sites, nodes, live productions and current alarms. ${VENDOR_ONLY}`,
    params: tenantPathWireSchema,
    query: null,
    body: null,
    response: vendorTenantDetailResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  createVendorTenant: {
    id: 'createVendorTenant',
    method: 'POST',
    path: '/vendor/tenants',
    tag: 'vendor',
    summary: 'Create a tenant and invite its first admin',
    description: `The console's "new tenant" (D39): the tenant, its default groups and its first admin — sent ACP's branded invite (\`invite.delivery\` says how it left) — and the staff's record of it: \`kind\` (\`customer\`, or \`demo\` for a demo tenant), a contact and internal notes, never shown to the tenant's own users. \`conflict\` when the tenant id or the admin's email exists. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: createVendorTenantRequestWireSchema,
    response: createdVendorTenantResponseWireSchema,
    successStatus: 201,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict'],
  },
  updateVendorTenant: {
    id: 'updateVendorTenant',
    method: 'PATCH',
    path: '/vendor/tenants/{tenantId}',
    tag: 'vendor',
    summary: 'Edit the staff’s record of a tenant',
    description: `The tenant's kind, contact and internal notes (D39) — the staff's own record, never returned to the tenant's users. \`ifUpdatedAt\` makes it conditional: \`conflict\` when someone saved since. ${VENDOR_ONLY}`,
    params: tenantPathWireSchema,
    query: null,
    body: updateVendorTenantRequestWireSchema,
    response: vendorTenantRecordResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  suspendTenant: {
    id: 'suspendTenant',
    method: 'POST',
    path: '/vendor/tenants/{tenantId}/suspend',
    tag: 'vendor',
    summary: 'Suspend a tenant',
    description:
      'Refuses the tenant’s users (every route answers `suspended`, within the authorizer’s five-minute cache) and its nodes’ site links, with `reason`. Nodes keep running their productions locally. Suspending a suspended tenant replaces the reason. The vendor tenant cannot be suspended (`conflict`). Vendor tenant only.',
    params: tenantPathWireSchema,
    query: null,
    body: suspendTenantRequestWireSchema,
    response: vendorTenantResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  reactivateTenant: {
    id: 'reactivateTenant',
    method: 'POST',
    path: '/vendor/tenants/{tenantId}/reactivate',
    tag: 'vendor',
    summary: 'Reactivate a suspended tenant',
    description: `Idempotent: an active tenant is returned as it is. \`note\` goes to the vendor's activity log. ${VENDOR_ONLY}`,
    params: tenantPathWireSchema,
    query: null,
    body: reactivateTenantRequestWireSchema,
    response: vendorTenantResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  getVendorOps: {
    id: 'getVendorOps',
    method: 'GET',
    path: '/vendor/ops',
    tag: 'vendor',
    summary: 'The ops dashboard',
    description: `Every node of every tenant (role, link, witness, packages, certificate, clock, last seen), every live production with its protection, and the alarms evaluated on the latest status (D27). ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: vendorOpsResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  getVendorReleases: {
    id: 'getVendorReleases',
    method: 'GET',
    path: '/vendor/releases',
    tag: 'vendor',
    summary: 'Releases, pins and the rollout',
    description: `The latest release, the recently published ones, the stage and site pins (D15), and for every node the release it is served and the one it holds. \`unavailable\` on a stack without a release store. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: vendorReleasesResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  getVendorClients: {
    id: 'getVendorClients',
    method: 'GET',
    path: '/vendor/clients',
    tag: 'vendor',
    summary: 'Client releases calling the API',
    description: `Every SPA and CLI release (\`x-acp-client\`, D37 §4.1) that called the cloud API in the last \`days\` days: its requests, the ones refused below the SPA floor (\`upgrade-required\`), and its calls to deprecated routes — the metric a removal waits on (§4.8, contracts/deprecations.md). ${VENDOR_ONLY}`,
    params: null,
    query: vendorClientsQueryWireSchema,
    body: null,
    response: vendorClientsResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  setStagePin: {
    id: 'setStagePin',
    method: 'PUT',
    path: '/vendor/pins/stage',
    tag: 'vendor',
    summary: 'Pin the whole stage to a release',
    description: `Every site without a site pin is served \`listHash\` instead of the latest (D15: a hold or a rollback). The list must be published (\`validation-failed\`). ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: setPinRequestWireSchema,
    response: pinResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  clearStagePin: {
    id: 'clearStagePin',
    method: 'DELETE',
    path: '/vendor/pins/stage',
    tag: 'vendor',
    summary: 'Clear the stage pin',
    description: `Idempotent; answers \`{ pin: null }\`. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: pinResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  setSitePin: {
    id: 'setSitePin',
    method: 'PUT',
    path: '/vendor/tenants/{tenantId}/pins/sites/{siteId}',
    tag: 'vendor',
    summary: 'Pin one site to a release',
    description: `The site's nodes are served \`listHash\` whatever the stage pin says. The pin is the tenant's (\`pins/tenants/<tenantId>/sites/<siteId>.json\`): site ids are unique only within a tenant. The site must exist in that tenant (\`not-found\`); the cloud site cannot be pinned (its tasks run their production's release, PLAN §9.2 — \`validation-failed\`), nor to an unpublished list. ${VENDOR_ONLY}`,
    params: sitePinPathWireSchema,
    query: null,
    body: setPinRequestWireSchema,
    response: pinResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  clearSitePin: {
    id: 'clearSitePin',
    method: 'DELETE',
    path: '/vendor/tenants/{tenantId}/pins/sites/{siteId}',
    tag: 'vendor',
    summary: 'Clear a site pin',
    description: `Removes the tenant's pin of the site, and a legacy pin of that site id (keyed by site id alone, from before pins were tenant-qualified) if one is there. Idempotent; answers \`{ pin: null }\`. ${VENDOR_ONLY}`,
    params: sitePinPathWireSchema,
    query: null,
    body: null,
    response: pinResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  clearNodeRejection: {
    id: 'clearNodeRejection',
    method: 'DELETE',
    path: '/vendor/tenants/{tenantId}/sites/{siteId}/nodes/{nodeId}/rejections/{listHash}',
    tag: 'vendor',
    summary: 'Clear a node’s rejection of a release',
    description: `D40: the node rolled \`listHash\` back (its supervisor: not ready, a production not reattached, died young) or refused it (its install gate), and does not install it again until a newer release is served — or until this clear, which its next manifest carries. Rejected again after the clear, it holds again. \`not-found\`: the node has no rejection of that release in force. ${VENDOR_ONLY}`,
    params: nodeRejectionPathWireSchema,
    query: null,
    body: null,
    response: clearNodeRejectionResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  getVendorRings: {
    id: 'getVendorRings',
    method: 'GET',
    path: '/vendor/rings',
    tag: 'vendor',
    summary: 'Rollout rings: every tenant’s and site’s',
    description: `D40: the rings in rollout order, every tenant’s ring (assigned, else \`general\`) and each site’s override. Vendor-only: a tenant’s own users never see their ring. \`unavailable\` on a stack without a release store. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: vendorRingsResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['unavailable'],
  },
  setTenantRing: {
    id: 'setTenantRing',
    method: 'PUT',
    path: '/vendor/tenants/{tenantId}/ring',
    tag: 'vendor',
    summary: 'Put a tenant in a rollout ring',
    description: `D40: its sites without an override follow it (\`rings/tenants/<tenantId>.json\` in the stage’s release store). The staff and demo tenants belong in \`internal\`. \`not-found\`: no such tenant. ${VENDOR_ONLY}`,
    params: tenantPathWireSchema,
    query: null,
    body: setRingRequestWireSchema,
    response: ringResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'unavailable', 'payload-too-large'],
  },
  setSiteRing: {
    id: 'setSiteRing',
    method: 'PUT',
    path: '/vendor/tenants/{tenantId}/sites/{siteId}/ring',
    tag: 'vendor',
    summary: 'Override one site’s rollout ring',
    description: `D40: a site is the smallest unit that shares servers (every studio on it runs on its state services), so the ring is per site, never per studio. The site must exist in that tenant (\`not-found\`); the cloud site follows its tenant’s ring (\`validation-failed\`). ${VENDOR_ONLY}`,
    params: sitePinPathWireSchema,
    query: null,
    body: setRingRequestWireSchema,
    response: ringResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'unavailable', 'payload-too-large'],
  },
  clearSiteRing: {
    id: 'clearSiteRing',
    method: 'DELETE',
    path: '/vendor/tenants/{tenantId}/sites/{siteId}/ring',
    tag: 'vendor',
    summary: 'Clear a site’s ring override',
    description: `D40: the site follows its tenant’s ring again. Idempotent; answers \`{ assignment: null }\`. ${VENDOR_ONLY}`,
    params: sitePinPathWireSchema,
    query: null,
    body: null,
    response: ringResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'unavailable'],
  },
  getVendorRollout: {
    id: 'getVendorRollout',
    method: 'GET',
    path: '/vendor/rollout',
    tag: 'vendor',
    summary: 'The current rollout, its rings’ gates and the history',
    description: `D40: the rollout the last publish started (\`current\`: null before one), each ring’s gate as the scheduled evaluator judges it now (servers updated of total, productions completed, rejections, critical alarms, soak left), the servers’ uncleared rejections of its release, and earlier rollouts, newest first. \`unavailable\` on a stack without a release store. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: vendorRolloutResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['unavailable'],
  },
  rolloutAction: {
    id: 'rolloutAction',
    method: 'POST',
    path: '/vendor/rollout/{action}',
    tag: 'vendor',
    summary: 'Pause, resume, advance, halt or roll back the rollout',
    description: `D40: \`pause\` (nothing more opens), \`resume\` (a paused or halted rollout rolls again — a rejection still in force halts it at the next pass), \`advance\` (open the next ring now; with every ring open, complete it), \`halt\` (servers on the new release keep it, no other gets it), \`rollback\` (every ring is served the previous release again; servers move between shows through the janitor, as with a pin). \`listHash\` names the rollout meant: \`conflict\` when the current one is another’s, when the action does not apply to its status, or when it changed meanwhile. ${VENDOR_ONLY}`,
    params: rolloutActionPathWireSchema,
    query: null,
    body: rolloutActionRequestWireSchema,
    response: rolloutResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'unavailable', 'payload-too-large'],
  },
  listVendorActivity: {
    id: 'listVendorActivity',
    method: 'GET',
    path: '/vendor/activity',
    tag: 'vendor',
    summary: 'The vendor’s own actions',
    description: `Every vendor write — tenants created, suspended and reactivated, pins set and cleared, rings set, rollout actions, announcements — newest first, with who did it. Kept one year. ${VENDOR_ONLY}`,
    params: null,
    query: listVendorActivityQueryWireSchema,
    body: null,
    response: listVendorActivityResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  listVendorAnnouncements: {
    id: 'listVendorAnnouncements',
    method: 'GET',
    path: '/vendor/announcements',
    tag: 'vendor',
    summary: 'Every announcement',
    description: `Past, current and scheduled, newest start first. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: listVendorAnnouncementsResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  createAnnouncement: {
    id: 'createAnnouncement',
    method: 'POST',
    path: '/vendor/announcements',
    tag: 'vendor',
    summary: 'Create an announcement or incident',
    description: `Shown in-app to the tenants it targets (\`tenantIds\`, or every tenant when null) while in force, live over the change channel; a public one (every tenant) also on the status page. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: createAnnouncementRequestWireSchema,
    response: announcementResponseWireSchema,
    successStatus: 201,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  getVendorAnnouncement: {
    id: 'getVendorAnnouncement',
    method: 'GET',
    path: '/vendor/announcements/{announcementId}',
    tag: 'vendor',
    summary: 'One announcement',
    description: VENDOR_ONLY,
    params: announcementPathWireSchema,
    query: null,
    body: null,
    response: announcementResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  updateAnnouncement: {
    id: 'updateAnnouncement',
    method: 'PATCH',
    path: '/vendor/announcements/{announcementId}',
    tag: 'vendor',
    summary: 'Edit an announcement',
    description: `Changes any of its fields; \`version\` is the one the edit was made from (\`conflict\` if it moved — D35's overwrite-or-discard). Ending an incident is setting \`endsAt\`. ${VENDOR_ONLY}`,
    params: announcementPathWireSchema,
    query: null,
    body: updateAnnouncementRequestWireSchema,
    response: announcementResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteAnnouncement: {
    id: 'deleteAnnouncement',
    method: 'DELETE',
    path: '/vendor/announcements/{announcementId}',
    tag: 'vendor',
    summary: 'Delete an announcement',
    description: `Gone from every tenant and the status page; returns what was deleted. ${VENDOR_ONLY}`,
    params: announcementPathWireSchema,
    query: null,
    body: null,
    response: announcementResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  listAnnouncements: {
    id: 'listAnnouncements',
    method: 'GET',
    path: '/announcements',
    tag: 'tenancy',
    summary: 'Announcements for my tenant',
    description: 'The vendor’s announcements in force now that target the caller’s tenant: the in-app banner. Any signed-in user.',
    params: null,
    query: null,
    body: null,
    response: listAnnouncementsResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: [],
  },
  listPublicAnnouncements: {
    id: 'listPublicAnnouncements',
    method: 'GET',
    path: '/public/announcements',
    tag: 'status',
    summary: 'Public announcements (the status page)',
    description:
      'Public announcements in force, scheduled, or ended within the last seven days, newest start first — what the status page shows. No credential: the status page’s synthetic check reads it.',
    params: null,
    query: null,
    body: null,
    response: listPublicAnnouncementsResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'none',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
